import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { UserPlus, TrendingUp, AlertCircle } from "lucide-react";

/**
 * Renders a compact SVG sparkline. All decisions are inline (no dep):
 *  - Fixed width/height keeps layout stable across widgets
 *  - Line + soft area fill for readability at small sizes
 *  - Emphasizes the last point with a solid dot so admins can spot "today"
 */
function Sparkline({ values, stroke = "#1A1A1A", fill = "rgba(26,26,26,0.08)", width = 200, height = 44 }) {
  const nums = Array.isArray(values) && values.length ? values.map((v) => Number(v) || 0) : [0];
  const max = Math.max(...nums, 1);
  const min = Math.min(...nums, 0);
  const range = max - min || 1;
  const step = nums.length > 1 ? width / (nums.length - 1) : width;
  const y = (v) => height - 4 - ((v - min) / range) * (height - 8);
  const points = nums.map((v, i) => `${(i * step).toFixed(2)},${y(v).toFixed(2)}`);
  const line = `M ${points.join(" L ")}`;
  const area = `${line} L ${width.toFixed(2)},${(height - 4).toFixed(2)} L 0,${(height - 4).toFixed(2)} Z`;
  const lastX = nums.length > 1 ? (nums.length - 1) * step : width / 2;
  const lastY = y(nums[nums.length - 1]);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" aria-hidden="true">
      <path d={area} fill={fill} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="2.5" fill={stroke} />
    </svg>
  );
}

function InsightsCard({ testId, Icon, label, value, sub, spark, stroke, fill }) {
  return (
    <div className="ed-card p-4 flex items-center justify-between gap-4" data-testid={testId}>
      <div className="min-w-0">
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 flex items-center gap-1.5">
          <Icon size={11} strokeWidth={1.75} /> {label}
        </p>
        <p className="font-serif text-3xl mt-2 leading-none" data-testid={`${testId}-value`}>{value}</p>
        {sub && <p className="font-mono text-[0.6rem] text-neutral-500 mt-1.5">{sub}</p>}
      </div>
      <div className="shrink-0">
        <Sparkline values={spark} stroke={stroke} fill={fill} />
      </div>
    </div>
  );
}

export default function AdminInsightsWidget() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const r = await api.get("/admin/insights/7d");
        if (!cancelled) setData(r.data);
      } catch { /* silent — widget just stays empty */ }
    };
    fetchData();
    const iv = setInterval(fetchData, 300000); // refresh every 5 min
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (!data) return null;

  const currency = new Intl.NumberFormat(lang === "en" ? "en-GB" : "nl-NL", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  });

  const pendingTotal =
    (data.pending_snapshot?.testimonials || 0) +
    (data.pending_snapshot?.tickets || 0) +
    (data.pending_snapshot?.failed_emails || 0);

  // Build a "pending spark" from the raw signals so the eye sees composition
  // rather than a flat line — cheap visual, no extra API call.
  const pendingSpark = [
    data.pending_snapshot?.testimonials || 0,
    data.pending_snapshot?.tickets || 0,
    data.pending_snapshot?.failed_emails || 0,
    pendingTotal,
  ];

  return (
    <section className="mt-8" data-testid="admin-insights-widget">
      <div className="flex items-baseline justify-between mb-3">
        <p className="ed-eyebrow">{t("admInsightsTitle")}</p>
        <p className="font-mono text-[0.6rem] text-neutral-400 hidden sm:block">{t("admInsightsHint")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InsightsCard
          testId="insights-new-users"
          Icon={UserPlus}
          label={t("admInsightsNewUsers")}
          value={data.totals?.new_users_7d ?? 0}
          sub={t("admInsightsSince")}
          spark={data.new_users_by_day || []}
          stroke="#1A1A1A"
          fill="rgba(26,26,26,0.08)"
        />
        <InsightsCard
          testId="insights-revenue"
          Icon={TrendingUp}
          label={t("admInsightsRevenue")}
          value={currency.format(data.totals?.revenue_7d ?? 0)}
          sub={t("admInsightsSince")}
          spark={data.revenue_by_day || []}
          stroke="#047857"
          fill="rgba(4,120,87,0.10)"
        />
        <InsightsCard
          testId="insights-pending"
          Icon={AlertCircle}
          label={t("admInsightsPending")}
          value={pendingTotal}
          sub={`${data.pending_snapshot?.testimonials || 0} · ${data.pending_snapshot?.tickets || 0} · ${data.pending_snapshot?.failed_emails || 0}`}
          spark={pendingSpark}
          stroke={pendingTotal > 0 ? "#be123c" : "#525252"}
          fill={pendingTotal > 0 ? "rgba(190,18,60,0.10)" : "rgba(82,82,82,0.06)"}
        />
      </div>
    </section>
  );
}
